import jsPDF from "jspdf";
import uuid from "@/app/infrastructures/misc/common-library/Uuid";
import { formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import { startCase } from "lodash";
import moment from "moment";
import { CargoV2Data } from "@/domain/entities/CargoV2";


let doc: any = new jsPDF();

export const generateSptjNonElectronic = async ({
  data,
  companyPlanes
}: {
  data: CargoV2Data;
  companyPlanes: { logo: string; label: string }[];
}): Promise<void> => {
  doc = new jsPDF("p", "mm", "a4", true);
  const currentLocale = moment.locale();
  moment.locale("id");

  const margin = {
    left: 5,
    right: 5,
    top: 5,
    bottom: 5
  };

  const widthPaper = doc.internal.pageSize.getWidth();
  const centerStart = widthPaper / 2;
  const gapY = 7;
  const gapYSpacing = gapY * 1.4;
  const lineHeightFactor = 1.5;

  const logoProperties = doc.getImageProperties(companyPlanes[0].logo);
  const logoAspectRatio = logoProperties.height / logoProperties.width;

  let y = margin.top;

  // START LOGO
  if (companyPlanes.length === 5) {
    const gapColumn = 6;
    const widthPerColumn =
      (widthPaper - margin.left - margin.right - gapColumn * 3) / 4;

    // LION AIR
    let startFillX = margin.left;
    let latestFillX = startFillX + widthPerColumn;

    doc.addImage(
      companyPlanes[0].logo,
      "PNG",
      startFillX,
      y,
      widthPerColumn,
      logoAspectRatio * widthPerColumn,
      uuid(),
      "FAST",
      0
    );

    // BATIK AIR
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    doc.addImage(
      companyPlanes[1].logo,
      "PNG",
      startFillX,
      y,
      widthPerColumn,
      logoAspectRatio * widthPerColumn,
      uuid(),
      "FAST",
      0
    );

    // SUPER AIR JET
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    doc.addImage(
      companyPlanes[2].logo,
      "PNG",
      startFillX,
      y,
      widthPerColumn,
      logoAspectRatio * widthPerColumn,
      uuid(),
      "FAST",
      0
    );

    // WINGS AIR
    startFillX = latestFillX + gapColumn;
    latestFillX = startFillX + widthPerColumn;

    doc.addImage(
      companyPlanes[3].logo,
      "PNG",
      startFillX,
      y,
      widthPerColumn,
      logoAspectRatio * widthPerColumn,
      uuid(),
      "FAST",
      0
    );

    // LION PARCEL
    const widthLionParcelLogo = widthPerColumn * 1.6;
    y += logoAspectRatio * widthPerColumn + 1;

    doc.addImage(
      companyPlanes[4].logo,
      "PNG",
      centerStart - widthLionParcelLogo / 2,
      y,
      widthLionParcelLogo,
      logoAspectRatio * widthLionParcelLogo,
      uuid(),
      "FAST",
      0
    );

    y += gapY + logoAspectRatio * widthLionParcelLogo;
  }
  // END LOGO

  // title
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(14);
  const title = "SURAT PERNYATAAN";
  const titleWidth = doc.getTextWidth(title);
  const titleStartX = centerStart - titleWidth / 2;

  doc.text(title, titleStartX, y);

  // title underline
  doc.setDrawColor("#000");
  doc.setLineWidth(0.2);
  doc.line(titleStartX, y + 1, titleStartX + titleWidth, y + 1);

  // subtitle
  doc.setFontSize(12);
  const subTitle = "PEMBEBASAN TANGGUNG JAWAB PENGANGKUTAN CARGO";
  const subTitleWidth = doc.getTextWidth(subTitle);
  const subTitleStartX = centerStart - subTitleWidth / 2;

  doc.text(subTitle, subTitleStartX, (y += gapY));

  // description 1
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(11);
  doc.text(
    "Saya yang bertanda tangan dibawah ini:",
    margin.left,
    (y += gapYSpacing)
  );

  const semicolonSeparator = 85;
  const tabValueX = semicolonSeparator + 2;

  // Nama pengirim
  doc.text("Nama", margin.left, (y += gapY));
  doc.text(":", semicolonSeparator, y);

  // Umur
  doc.text("Umur", margin.left, (y += gapY));
  doc.text(":", semicolonSeparator, y);

  // alamat pengirim
  const senderAddress = "";
  doc.text("Alamat", margin.left, (y += gapY));

  const senderAddressBox = widthPaper - tabValueX - margin.left - margin.right;
  const senderAddressLines = doc.splitTextToSize(
    senderAddress,
    senderAddressBox
  );
  doc.text(": ", semicolonSeparator, y);
  doc.text(senderAddressLines, tabValueX, y, {
    lineHeightFactor
  });

  const senderAddressLineHeight =
    (doc.getFontSize(senderAddressLines[0]) * lineHeightFactor) / 3;
  y += senderAddressLineHeight * senderAddressLines.length + 1.4;

  // no telepon/hp
  doc.text("No Telepon/Hp", margin.left, y);
  doc.text(":", semicolonSeparator, y);

  // deskripsi 2
  const description2 =
    "Akan melaksanakan pengiriman barang kargo dengan penerbangan Lion Air/ Batik Air/ Wings Air/ Super Air Jet.";
  const description2Box = widthPaper - margin.left - margin.right - 10;
  const description2Lines = doc.splitTextToSize(description2, description2Box);

  doc.setFont("Montserrat", "bold");
  doc.text(description2Lines, margin.left, (y += gapY), {
    lineHeightFactor
  });

  const description2LineHeight =
    (doc.getFontSize(description2Lines[0]) * lineHeightFactor) / 3;
  y += description2LineHeight * description2Lines.length + 1.6;

  doc.setFont("Montserrat", "normal");

  // Nama penerima
  doc.text("Nama Penerima", margin.left, y);
  doc.text(`: Lion Parcel ${data.destinationCityCode}`, semicolonSeparator, y);

  // Isi Barang/kargo
  doc.text("Isi Barang/kargo", margin.left, (y += gapY));
  doc.text(`: ${data.nog}`, semicolonSeparator, y);

  // Jumlah Koli/kg
  doc.text("Jumlah Koli/kg", margin.left, (y += gapY));
  doc.text(
    `: ${formatByCountry(
      data.totalPieces,
      { type: "quantity" }
    )} Koli / ${formatByCountry(
      parseByCountry(data.totalActualCargoGrossWeight),
      { type: "weight" }
    )} Kg`,
    semicolonSeparator,
    y
  );

  // Asal Pengiriman
  doc.text("Asal Pengiriman", margin.left, (y += gapY));
  doc.text(`: Lion Parcel ${data.originCityCode}`, semicolonSeparator, y);

  // Kondisi Barang
  doc.text("Kondisi Barang", margin.left, (y += gapY));
  doc.text(": BAIK & KARDUS", semicolonSeparator, y);

  // No. AWB/SMU
  doc.text("No. AWB/SMU", margin.left, (y += gapY));
  doc.text(`: ${data.cargoNumber}`, semicolonSeparator, y);

  // Statement Title
  doc.setFont("Montserrat", "bold");
  doc.setFontSize(13);
  const statementTitle = "MENYATAKAN BAHWA";
  const statementTitleWidth = doc.getTextWidth(statementTitle);
  const statementTitleStartX = centerStart - statementTitleWidth / 2;

  y += gapY * 1.5;

  // Statement Title topline
  doc.setDrawColor("#000");
  doc.setLineWidth(0.2);
  doc.line(
    statementTitleStartX - 5,
    y - 5.5,
    statementTitleStartX + statementTitleWidth + 6,
    y - 5.5
  );

  doc.text(statementTitle, statementTitleStartX, y);

  // Statement Title underline
  doc.setDrawColor("#000");
  doc.setLineWidth(0.2);
  y += 2;
  doc.line(
    statementTitleStartX - 5,
    y,
    statementTitleStartX + statementTitleWidth + 6,
    y
  );

  // Statement
  doc.setFont("Montserrat", "normal");
  doc.setFontSize(11);
  y += gapY;

  // Statement 1
  const statement1 =
    "Dengan ini saya menyatakan membebaskan PT. Lion Express dari segala bentuk tanggung jawab atas segala akibat buruk pengangkutan barang / kargo milik kami tersebut diatas.";
  const statement1StartX = margin.left + 5;

  const statement1Box = widthPaper - statement1StartX - margin.right;
  const statement1Lines = doc.splitTextToSize(statement1, statement1Box);
  doc.text("1.", margin.left, y);
  doc.text(statement1Lines, statement1StartX, y, {
    lineHeightFactor
  });

  const statement1LineHeight =
    (doc.getFontSize(statement1Lines[0]) * lineHeightFactor) / 3;
  y += statement1LineHeight * statement1Lines.length + 1.4;

  // Statement 2
  const statement2 =
    "Dalam hubungan dengan pengangkutan ini, kami telah menyetujui :";
  const statement2StartX = margin.left + 5;

  const statement2Box = widthPaper - statement2StartX - margin.right;
  const statement2Lines = doc.splitTextToSize(statement2, statement2Box);
  doc.text("2.", margin.left, y);
  doc.text(statement2Lines, statement2StartX, y, {
    lineHeightFactor
  });

  const statement2LineHeight =
    (doc.getFontSize(statement2Lines[0]) * lineHeightFactor) / 3;
  y += statement2LineHeight * statement2Lines.length + 1.4;

  // Statement 2.1
  const statement21 =
    "Membebaskan pengangkut dari tanggung jawab atas kerugian-kerugian yang bagaimanapun juga yang ditimbulkan oleh kelambatan dalam pengangkutan, termasuk juga kelambatan datang serta kelambatan dalam penyerahan barang - barang.";
  const statement21PointerStartX = margin.left + 6.5;
  const statement21StartX = statement21PointerStartX + 4;

  const statement21Box = widthPaper - statement21StartX - margin.right;
  const statement21Lines = doc.splitTextToSize(statement21, statement21Box);

  // point 2.1
  doc.circle(statement21PointerStartX, y - 1, 0.5, "F");

  doc.text(statement21Lines, statement21StartX, y, {
    lineHeightFactor
  });

  const statement21LineHeight =
    (doc.getFontSize(statement21Lines[0]) * lineHeightFactor) / 3;
  y += statement21LineHeight * statement21Lines.length + 1.4;

  // Statement 2.2
  const statement22 =
    "Membebaskan pengangkut dari tanggung jawab atas hilang sebagian atau seluruh dan atau sampai tujuan barang mudah pecah/atau rusak yang tersebut diatas.";
  const statement22PointerStartX = margin.left + 6.5;
  const statement22StartX = statement22PointerStartX + 4;

  const statement22Box = widthPaper - statement22StartX - margin.right;
  const statement22Lines = doc.splitTextToSize(statement22, statement22Box);

  // point 2.2
  doc.circle(statement22PointerStartX, y - 2, 0.5, "F");

  doc.text(statement22Lines, statement22StartX, y, {
    lineHeightFactor
  });

  const statement22LineHeight =
    (doc.getFontSize(statement22Lines[0]) * lineHeightFactor) / 3;
  y += statement22LineHeight * statement22Lines.length + 1.4;

  // Statement 3
  const statement3 =
    "Segala biaya yang timbul, baik langsung maupun tidak langsung selama hubungan pengangkutan berlangsung dari tempat asal sampai tempat tujuan, menjadi tanggung jawab kami.";
  const statement3StartX = margin.left + 5;

  const statement3Box = widthPaper - statement3StartX - margin.right;
  const statement3Lines = doc.splitTextToSize(statement3, statement3Box);
  doc.text("3.", margin.left, y);
  doc.text(statement3Lines, statement3StartX, y, {
    lineHeightFactor
  });

  const statement3LineHeight =
    (doc.getFontSize(statement3Lines[0]) * lineHeightFactor) / 3;
  y += statement3LineHeight * statement3Lines.length + 1.4;

  // Statement 4
  const statement4 =
    "Lion Air/wings Air, termasuk agen-agen kargo serta karyawannya dibebaskan dari segala tuntutan kerugian, atau biaya yang timbul atas kerusakan barang/kargo milik kami.";
  const statement4StartX = margin.left + 5;

  const statement4Box = widthPaper - statement4StartX - margin.right;
  const statement4Lines = doc.splitTextToSize(statement4, statement4Box);
  doc.text("4.", margin.left, y);
  doc.text(statement4Lines, statement4StartX, y, {
    lineHeightFactor
  });

  const statement4LineHeight =
    (doc.getFontSize(statement4Lines[0]) * lineHeightFactor) / 3;
  y += statement4LineHeight * statement2Lines.length + 1.4;

  y += 5;
  doc.text("Demikian surat pernyataan ini kami buat dengan sebenar-benar nya.", margin.left + 5, y);

  // footer signature
  const boxSignature = 80;
  const gapYSignature = 6;

  // current date
  const currentDate = `${startCase(
    data.originCityName.toLowerCase()
  )}, ${moment().format("DD MMMM YYYY")}`;
  const widthCurrentDate = doc.getTextWidth(currentDate);

  doc.text(
    currentDate,
    widthPaper - margin.right - boxSignature / 2 - widthCurrentDate / 2,
    (y += gapYSpacing)
  );

  // officer
  const airlinesofficerTitle = "Petugas Airlines";
  const widthAirlinesOfficerTitle = doc.getTextWidth(airlinesofficerTitle);

  doc.text(
    airlinesofficerTitle,
    margin.left + boxSignature / 2 - widthAirlinesOfficerTitle / 2,
    (y += gapYSignature)
  );

  // concerned title
  const concernedTitle = "Yang Bersangkutan";
  const widthConcernedTitle = doc.getTextWidth(concernedTitle);

  doc.text(
    concernedTitle,
    widthPaper - margin.right - boxSignature / 2 - widthConcernedTitle / 2,
    y
  );

  // materai
  doc.text(
    "",
    widthPaper - margin.right - boxSignature / 2,
    (y += gapYSignature)
  );

  // materai 10.000
  doc.text(
    "",
    widthPaper - margin.right - boxSignature / 2,
    (y += gapY * 1.8)
  );

  // fill Officer name
  const fillName =
    "(......................................................................)";
  const widthFillName = doc.getTextWidth(fillName);

  doc.text(
    fillName,
    margin.left + boxSignature / 2 - widthFillName / 2,
    (y += gapY)
  );

  // fill concerned name
  doc.text(
    fillName,
    widthPaper - margin.right - boxSignature / 2 - widthFillName / 2,
    y
  );

  // concerned name
  const concernedName = "Nama yang bersangkutan";
  const widthConcernedName = doc.getTextWidth(concernedName);

  doc.text(
    concernedName,
    widthPaper - margin.right - boxSignature / 2 - widthConcernedName / 2,
    (y += gapYSignature)
  );

  // NB
  doc.setFont("Montserrat", "bolditalic");
  doc.text(
    "NB: 1 lembar untuk file STO asal & 1 lembar untuk STO tujuan",
    margin.left,
    (y += gapY)
  );
  doc.text("-CHECK FISIK DAN PACKING", margin.left, (y += gapYSignature));

  doc.setProperties({
    title: `SURAT PEMBEBASAN TANGGUNG JAWAB PENGANGKUTAN CARGO LION PARCEL ${data.originCityCode} - ${data.cargoNumber}`
  });

  doc.autoPrint();

  window.open(doc.output("bloburl"), "_blank");

  doc = new jsPDF("p", "mm", "a6", true);
  y = margin.top;

  moment.locale(currentLocale);
};
